import React from 'react'

import {
  Box,
  Icon,
  Section,
  Spinner,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import BaseRule from 'modules/attendance/components/SettingItemPage/TimeTracking/WorkTimeCategories/Content/HoursRules/BaseRule'
import CustomRule from 'modules/attendance/components/SettingItemPage/TimeTracking/WorkTimeCategories/Content/HoursRules/CustomRule'
import HoursRulesWizard from 'modules/attendance/components/SettingItemPage/TimeTracking/WorkTimeCategories/Content/HoursRulesWizard'
import TimeSettingsCallout from 'modules/attendance/components/SettingItemPage/TimeTracking/WorkTimeCategories/Content/TimeSettingsCallout'
import { useAttendanceSettingWorkCategories } from 'modules/attendance/lib/attendanceSettingWorkCategoriesContext'
import { EMPLOYEE_TYPE } from 'modules/attendance/lib/types'
import ExtraHoursRangeUtils from 'modules/attendance/lib/utils/ExtraHoursRangeUtils'

const ComplementaryHoursRules = () => {
  const [openBaseInfo, toggleOpenBaseInfo] = useToggle()
  const [openCustomInfo, toggleOpenCustomInfo] = useToggle()
  const [newComplementaryHourRuleModal, setNewComplementaryHourRuleModal] = React.useState(false)
  const { complementaryHoursRanges, loading, attendanceSetting } =
    useAttendanceSettingWorkCategories()
  const customComplementaryHoursRanges =
    ExtraHoursRangeUtils.sortedCustomByFromMinute(complementaryHoursRanges)
  const defaultComplementaryHoursRange =
    ExtraHoursRangeUtils.defaultExtraHoursRanges(complementaryHoursRanges)

  const latestToMinute = customComplementaryHoursRanges.at(-1)?.toMinute
  const fromHourRange = latestToMinute ? latestToMinute / 60 + 1 : 1

  if (loading)
    return (
      <Box justifyContent="center" alignItems="center" height="full">
        <Spinner />
      </Box>
    )

  return (
    <Section
      illustration="clock-app"
      title={i18n.t('time_tracking.settings.work_time_categories.complementary_hours_rules.title')}
      description={i18n.t(
        'time_tracking.settings.work_time_categories.complementary_hours_rules.description'
      )}
      action={{
        onClick: () => {
          setNewComplementaryHourRuleModal(true)
          track(trackingEvents.timeTracking.newExtraHourRulePressed)
        },
        label: i18n.t(
          'time_tracking.settings.work_time_categories.complementary_hours_rules.new_complementary_hour_rules'
        ),
      }}
    >
      <Box gap="s24">
        <Box gap="s12">
          <Box flexDirection="row">
            <Text color="grey1000" size="300" weight="semibold">
              {i18n.t(
                'time_tracking.settings.work_time_categories.complementary_hours_rules.base_rule.title'
              )}
            </Text>
            <Tooltip.Alt
              attachment="bottom center"
              targetAttachment="top center"
              open={openBaseInfo}
              toggle={toggleOpenBaseInfo}
              label={i18n.t(
                'time_tracking.settings.work_time_categories.complementary_hours_rules.base_rule.tooltip'
              )}
            >
              <Icon.Medium icon="question-circle-line" color="grey800" />
            </Tooltip.Alt>
          </Box>
          {defaultComplementaryHoursRange && <BaseRule rule={defaultComplementaryHoursRange} />}
        </Box>
        {ExtraHoursRangeUtils.hasCustomExtraHoursRanges(complementaryHoursRanges) && (
          <Box gap="s8">
            <Box flexDirection="row">
              <Text color="grey1000" size="300" weight="semibold">
                {i18n.t(
                  'time_tracking.settings.work_time_categories.common.rules.custom_rule.title'
                )}
              </Text>
              <Tooltip.Alt
                attachment="bottom center"
                targetAttachment="top center"
                open={openCustomInfo}
                toggle={toggleOpenCustomInfo}
                label={i18n.t(
                  'time_tracking.settings.work_time_categories.common.rules.custom_rule.tooltip'
                )}
              >
                <Icon.Medium icon="question-circle-line" color="grey800" />
              </Tooltip.Alt>
            </Box>
            {customComplementaryHoursRanges.map((complementaryHourRule) => (
              <CustomRule key={complementaryHourRule.id} rule={complementaryHourRule} />
            ))}
          </Box>
        )}
      </Box>
      <Box paddingTop="s16">
        {ExtraHoursRangeUtils.hasTimeOffRanges(complementaryHoursRanges) && (
          <TimeSettingsCallout attendanceSetting={attendanceSetting} />
        )}
      </Box>
      {newComplementaryHourRuleModal && (
        <HoursRulesWizard
          onClose={() => setNewComplementaryHourRuleModal(false)}
          fromHourRange={fromHourRange}
          employeeType={EMPLOYEE_TYPE.PART_TIME}
        />
      )}
    </Section>
  )
}

export default ComplementaryHoursRules
